import React, { Component } from 'react';
import Phone, { isValidPhoneNumber } from 'react-phone-number-input';
import { Form } from 'react-form';
import { Link } from 'react-router-dom';
import requireAuth from '../hocs/requireAuth';
import {
    Input,
    ToggleSwitch,
    PasswordValidation,
} from '../Form';
import { optionalEmail, requiredEmail } from '../../utils/validation';
import { resendEmailConfirmation } from '../../api/emailConfirmationApi';
import FeedPreferencesForm from './FeedPreferencesForm';
import AustraliaForm from './AustraliaForm';
import { getAgeKeyByValue, defaultPhoneCountryCode } from '../../utils/constants';
import { FeedPreferenceGameLocations } from '../../utils/settings';
import config from '../../../config';
import { removeUtSuffixIdentifier } from '../../utils/email';

import styles from './settingsForm.scss';
import EmailConfirmationAlert from '../EmailConfirmationAlert';
import { getApiAxiosInstance } from '../../utils/network';
import { linkedAccountTypes } from '../../utils/oauth';

class SettingsForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            phoneError: null,
            secondaryAlertDisplay: false,
            toggleSecondaryAlert: true,
            lastChangedField: null,
        };
        this.enableSave = this.enableSave.bind(this);
        this.handleEmailConfirmation = this.handleEmailConfirmation.bind(this);
        this.handleCloseAlert = this.handleCloseAlert.bind(this);
    }

    enableSave(values) {
        const { user, feedPreferences, australiaSettings, settings } = this.props.auth;
        const isAustralian = this.props.auth?.user?.isAustralian;
        const isEmpty = value => value === undefined || value === null || value === '';
        let enableSave = false;

        let userPrefUpdated = false;
        let feedPrefUpdated = false;
        let australiaPrefUpdated = false;

        if (user) {
            const isEmailUpdated = values.email !== user.email;
            const isPhoneUpdated = values.phone !== user.phone && !this.state.phoneError;
            const passwordAdded = values.password && values.password.length > 0;
            const passwordEqual = values.password === values.confirmPassword;
            const isSecondaryEmailUpdated = values.secondaryEmail !== user.secondaryEmail;
            const isSmsOptInUpdated = values.smsOptIn !== settings?.smsOptIn;
            userPrefUpdated = (passwordAdded && passwordEqual) || isEmailUpdated || isPhoneUpdated || isSecondaryEmailUpdated || !isEmpty(values.currentPassword) || isSmsOptInUpdated;
        }

        if (feedPreferences) {
            const isResultsCountToMyUtrUpdated = values.resultsCountToMyUtr !== feedPreferences.resultsCountToMyUtr;
            const isRecreationalEventsUpdated = values.recreationalEvents !== feedPreferences.recreationalEvents;
            const isNonScoringEventsUpdated = values.nonScoringEvents !== feedPreferences.nonScoringEvents;
            const isGameLocationUpdated = values.gameLocation !== feedPreferences.gameLocation;
            const isDistanceUpdated = values.distance !== feedPreferences.distance;
            const isGenderUpdated = values.gender !== feedPreferences.gender;
            const isUtrRangeUpdated = values.utrRange[0] !== feedPreferences.utrMin || values.utrRange[1] !== feedPreferences.utrMax;
            const ageMin = parseInt(getAgeKeyByValue(parseInt(feedPreferences?.ageMin) || 1));
            const ageMax = parseInt(getAgeKeyByValue(parseInt(feedPreferences?.ageMax) || 100));
            const isAgeRangeUpdated = values.ageRange[0] !== ageMin || values.ageRange[1] !== ageMax;

            feedPrefUpdated = isResultsCountToMyUtrUpdated || isRecreationalEventsUpdated || isNonScoringEventsUpdated ||
                isGameLocationUpdated || isDistanceUpdated || isGenderUpdated || isUtrRangeUpdated || isAgeRangeUpdated;
        }

        if (isAustralian && australiaSettings) {
            const isTennisConnectIdUpdated = values.tennisConnectId !== australiaSettings.tennisConnectId;
            const isMarketingEmailOptInUpdated = values.marketingEmailsOptIn !== australiaSettings.marketingEmailsOptIn;
            australiaPrefUpdated = isTennisConnectIdUpdated || isMarketingEmailOptInUpdated;
        }

        if (userPrefUpdated || feedPrefUpdated || australiaPrefUpdated) {
            enableSave = true;
        }

        return enableSave;
    }

    handleEmailConfirmation() {
        this.setState({
            secondaryAlertDisplay: true,
        });
        resendEmailConfirmation();
    }

    handleCloseAlert() {
        this.setState({
            toggleSecondaryAlert: false,
        });
    }

    render() {
        const {
            auth,
            submitting,
            handleSubmit,
            apiEmailError,
            onEmailChange,
            updateMatchPlayAvailability,
        } = this.props;

        const isEmpty = value => value === undefined || value === null || value === '';

        const checkPhone = (value) => {
            let error = null;
            if (!isEmpty(value) && !isValidPhoneNumber(value)) {
                error = 'Please provide a valid phone number.';
            }
            this.setState({
                phoneError: error,
            });
        };

        const checkPassword = (value, allowEmpty = true) => {
            let error;
            if (isEmpty(value)) {
                if (!allowEmpty) {
                    error = 'This field is required.';
                }
            }
            if (/[^a-zA-Z0-9]/.test(value)) {
                error = 'Special characters not allowed.';
            }
            return {
                error,
            };
        };

        const checkReEnteredPassword = (value, password) => {
            let error;
            if (password && !value) {
                error = 'This Field is required';
            } else if (password && value) {
                if (password !== value) {
                    error = 'The passwords do not match';
                }
            }
            return {
                error,
            };
        };

        const hasClubSettings = auth?.settings?.clubSettings;
        const defaultValues = {
            // settings
            email: removeUtSuffixIdentifier(auth?.settings?.email),
            phone: auth?.settings?.phone,
            phoneCountryCode: auth?.settings?.phoneCountryCode ?? defaultPhoneCountryCode,
            secondaryEmail: auth?.settings?.secondaryEmail,
            playAvailability: auth?.settings?.clubSettings?.playAvailability?.find(c => c.value === 'all'),
            smsOptIn: auth?.settings?.smsOptIn,
            // feed preferences
            utrMin: auth?.feedPreferences?.utrMin || 1,
            utrMax: auth?.feedPreferences?.utrMax || 16,
            resultsCountToMyUtr: auth?.feedPreferences?.resultsCountToMyUtr ?? true,
            recreationalEvents: auth?.feedPreferences?.recreationalEvents ?? true,
            nonScoringEvents: auth?.feedPreferences?.nonScoringEvents ?? true,
            gender: auth?.feedPreferences?.gender || 'all',
            ageMin: parseInt(auth?.feedPreferences?.ageMin) || 1,
            ageMax: parseInt(auth?.feedPreferences?.ageMax) || 100,
            gameLocation: auth?.feedPreferences?.gameLocation || FeedPreferenceGameLocations.GPS,
            distance: auth?.feedPreferences?.distance || 50,
            receiveInvitationsFrom: [],
            showSuggestedContentFrom: [],
            showPickleballContent: auth?.feedPreferences?.showPickleballContent,
            showTennisContent: auth?.feedPreferences?.showTennisContent,
        };
        defaultValues.ageRange = [1, 17];
        defaultValues.ageRange = [parseInt(getAgeKeyByValue(defaultValues.ageMin)), parseInt(getAgeKeyByValue(defaultValues.ageMax))];
        defaultValues.utrRange = [defaultValues.utrMin, defaultValues.utrMax];
        defaultValues.gameLocationGps = defaultValues.gameLocation === FeedPreferenceGameLocations.GPS;
        defaultValues.gameLocationProfile = defaultValues.gameLocation === FeedPreferenceGameLocations.PROFILE;

        const isAustralian = auth?.user?.isAustralian;

        if (isAustralian || this.props.isTennisAustraliaAccountLinked) {
            defaultValues.tennisConnectId = auth?.australiaSettings?.tennisConnectId;
            defaultValues.marketingEmailsOptIn = auth?.australiaSettings?.marketingEmailsOptIn;
            defaultValues.linkAustraliaAccount = this.props.isTennisAustraliaAccountLinked;
        }

        // Set Apple account link status
        defaultValues.linkAppleAccount = this.props.isAppleAccountLinked;

        // Set Facebook account link status
        defaultValues.linkFacebookAccount = this.props.isFacebookAccountLinked;

        const onKeyPress = (event) => {
            if (event.key === 'Enter' && event.target.type !== 'submit' && event.target.type !== 'button' && event.target.id !== 'description') {
                event.preventDefault();
            }
        };

        // Add handler for Tennis Australia Account linking
        const handleLinkAustraliaAccount = async (checked, setValue) => {
            if (checked) {
                try {
                    const returnTo = `${config.appHost}/settings/account`;
                    window.location.href = `${config.apiHost}/v1/oauth/ta/initiate-link?platform=web&source=internal&flow=link&returnTo=${returnTo}`;
                } catch (error) {
                    // Optionally handle error
                    this.props.showErrorPopup({ title: error.response?.data?.DisplayMessage, message: 'Error linking Tennis Australia account', showSupportLink: true });
                    setValue('linkAustraliaAccount', false);
                }
            } else {
                const tennisAustraliaAccount = this.props.linkedAccounts.find(acc => acc.type === linkedAccountTypes.TENNIS_AUSTRALIA);
                try {
                    const axiosInstance = getApiAxiosInstance();
                    await axiosInstance.delete(`v1/oauth/linked-accounts/${tennisAustraliaAccount?.id}`);
                    // Refresh linked accounts after successful unlink
                    if (this.props.onLinkedAccountsChange) {
                        this.props.onLinkedAccountsChange();
                    }
                } catch (error) {
                    this.props.showErrorPopup({
                        title: error.response?.data?.DisplayMessage,
                        message: 'You must have at least one authentication method linked to your account',
                        showSupportLink: true,
                    });
                    setValue('linkAustraliaAccount', true);
                }
            }
        };

        // Add handler for Apple Account linking
        const handleLinkAppleAccount = async (checked, setValue) => {
            if (checked) {
                try {
                    const returnTo = `${config.appHost}/settings/account`;
                    window.location.href = `${config.apiHost}/v3/oauth/apple/initiate-link?platform=web&flow=link&returnTo=${returnTo}`;
                } catch (error) {
                    this.props.showErrorPopup({ title: error.response?.data?.DisplayMessage, message: 'Error linking Apple account', showSupportLink: true });
                    setValue('linkAppleAccount', false);
                }
            } else {
                const appleAccount = this.props.linkedAccounts.find(acc => acc.type === linkedAccountTypes.APPLE);
                try {
                    const axiosInstance = getApiAxiosInstance();
                    await axiosInstance.delete(`v1/oauth/linked-accounts/${appleAccount?.id}`);
                    // Refresh linked accounts after successful unlink
                    if (this.props.onLinkedAccountsChange) {
                        this.props.onLinkedAccountsChange();
                    }
                } catch (error) {
                    this.props.showErrorPopup({
                        title: error.response?.data?.DisplayMessage,
                        message: 'You must have at least one authentication method linked to your account.',
                        showSupportLink: true,
                    });
                    setValue('linkAppleAccount', true);
                }
            }
        };

        // Add handler for Facebook Account linking
        const handleLinkFacebookAccount = async (checked, setValue) => {
            if (checked) {
                try {
                    const returnTo = `${config.appHost}/settings/account`;
                    window.location.href = `${config.apiHost}/v3/oauth/facebook/initiate-link?platform=web&flow=link&returnTo=${returnTo}`;
                } catch (error) {
                    this.props.showErrorPopup({ title: error.response?.data?.DisplayMessage, message: 'Error linking Facebook account', showSupportLink: true });
                    setValue('linkFacebookAccount', false);
                }
            } else {
                const facebookAccount = this.props.linkedAccounts.find(acc => acc.type === linkedAccountTypes.FACEBOOK);
                try {
                    const axiosInstance = getApiAxiosInstance();
                    await axiosInstance.delete(`v1/oauth/linked-accounts/${facebookAccount?.id}`);
                    // Refresh linked accounts after successful unlink
                    if (this.props.onLinkedAccountsChange) {
                        this.props.onLinkedAccountsChange();
                    }
                } catch (error) {
                    this.props.showErrorPopup({
                        title: error.response?.data?.DisplayMessage,
                        message: 'You must have at least one authentication method linked to your account.',
                        showSupportLink: true,
                    });
                    setValue('linkFacebookAccount', true);
                }
            }
        };

        return (
            <Form
                onSubmit={values => handleSubmit(values)}
                defaultValues={defaultValues}
            >
                {
                    (formApi) => {
                        const { submitForm, values, setValue } = formApi;
                        const { isTennisAustraliaLoginActive } = this.props;
                        return (
                            <form onSubmit={submitForm} id="settingsForm" onKeyPress={onKeyPress} className={`${styles.settingsForm}`}>
                                <div className="container">
                                    <div className={`${styles.sportTypesPreferenceSection}`}>
                                        <div className={`${styles.sportSettingsHeading}`}>
                                            <h5 className={`text-uppercase`}>Sport Settings</h5>
                                            <p>This will customize the content you see when logged into UTR Sports.</p>
                                        </div>
                                        <div className={`${styles.twoSportTypes}`}>

                                            <div className={`${styles.singleSportType}`}>
                                                <div className={` ${styles.sportTypeDetails}`}>
                                                    <img src="/img/icons/pickleball_icon.svg" alt="" className="mb8" />
                                                    <p>Pickleball</p>
                                                </div>
                                                <ToggleSwitch
                                                    id={'showPickleballContent'}
                                                    field={'showPickleballContent'}
                                                    formGroupCssClass={`mt16 mb16`}
                                                    label="Pickleball"
                                                    description="Shows you pickleball events and players near you."
                                                    onChange={(currentValue, ev) => {
                                                        this.setState({ lastChangedField: ev.currentTarget.id });
                                                    }}
                                                    isSportTypeToggleSwitch={true}
                                                    data-testid="settings.sport-settings.pickleball-toggle"
                                                />
                                            </div>
                                            <div className={`${styles.singleSportType}`}>
                                                <div className={` ${styles.sportTypeDetails}`}>
                                                    <img src="/img/icons/tennis_icon.svg" alt="" className="mb8" />
                                                    <p>Tennis</p>
                                                </div>
                                                <ToggleSwitch
                                                    id={'showTennisContent'}
                                                    field={'showTennisContent'}
                                                    formGroupCssClass={`mt16 mb16`}
                                                    label="Tennis"
                                                    description="Shows you tennis events and players near you."
                                                    onChange={(currentValue, ev) => {
                                                        this.setState({ lastChangedField: ev.currentTarget.id });
                                                    }}
                                                    isSportTypeToggleSwitch={true}
                                                    data-testid="settings.sport-settings.tennis-toggle"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <FeedPreferencesForm
                                        formApi={formApi}
                                        auth={auth}
                                        lastChangedField={this.state.lastChangedField}
                                    />
                                    <div className="row bt mt16 pt40">
                                        <div className="col-md-4">
                                            <h5 className={`text-uppercase`}>Account</h5>
                                        </div>
                                        <div className="col-md-6 mt56">
                                            <Input
                                                label="Email"
                                                id="email"
                                                field="email"
                                                type="text"
                                                description="This is also your login username."
                                                placeholder="Email address"
                                                onChange={onEmailChange}
                                                validate={requiredEmail}
                                                disableProfanityCheck={true}
                                                data-testid="settings.account.email-input"
                                            />
                                            {auth && auth.user && (
                                                <Input
                                                    label="Parent/Guardian Email"
                                                    id="secondaryEmail"
                                                    field="secondaryEmail"
                                                    type="text"
                                                    description="An email address of a parent or guardian that will be verified and receive copies of the messages you receive on UTR Sports."
                                                    placeholder="Email address"
                                                    validate={optionalEmail}
                                                    disableProfanityCheck={true}
                                                    data-testid="settings.account.secondary-email-input"
                                                />
                                            )}
                                            <label htmlFor="phoneNumber">Phone Number</label>
                                            <div className="form-group form-group-xl">
                                                <Phone
                                                    id="phoneNumber"
                                                    field="phone"
                                                    value={values.phone}
                                                    className={`form-control ${styles.input}`}
                                                    placeholder="Mobile phone"
                                                    type={'tel'}
                                                    nativeCountrySelect={true}
                                                    country={values?.phoneCountryCode ?? defaultPhoneCountryCode}
                                                    flagsPath="/img/flags/"
                                                    onChange={(phone) => {
                                                        setValue('phone', phone);
                                                        checkPhone(phone);
                                                    }}
                                                    onCountryChange={(country) => {
                                                        setValue('phoneCountryCode', country);
                                                    }}
                                                />
                                                <ToggleSwitch
                                                    id={'smsOptIn'}
                                                    field={'smsOptIn'}
                                                    formGroupCssClass={`mt16 mb16`}
                                                    label="Allow SMS texting"
                                                    description="Allow organizers to send you SMS messages about events you're registered to."
                                                    onChange={(currentValue, ev) => {
                                                        this.setState({ lastChangedField: ev.currentTarget.id });
                                                    }}
                                                    data-testid="settings.account.sms-opt-in-toggle"
                                                />
                                            </div>
                                            <div className={`${styles.customError}`}>{this.state.phoneError}</div>
                                            <Input
                                                label="Current Password"
                                                id="currentPassword"
                                                field="currentPassword"
                                                type="password"
                                                placeholder="Current Password"
                                                disableProfanityCheck={true}
                                                data-testid="settings.account.current-password-input"
                                            />
                                            <div className={`${styles.customError}`}>{apiEmailError}</div>
                                            <div className="mb-4">
                                                <Link to="/forgotpassword" data-testid="settings.forgot-password-link">Forgot Password?</Link>
                                            </div>
                                            <Input
                                                label="New Password"
                                                id="password"
                                                field="password"
                                                type="password"
                                                placeholder="Enter new Password"
                                                validate={checkPassword}
                                                data-testid="settings.account.new-password-input"
                                                hoverText={(
                                                    <div>
                                                        Password should contain
                                                        <ul>
                                                            <li>
                                                                At least 8 characters
                                                            </li>
                                                            <li>
                                                                At least 1 uppercase character
                                                            </li>
                                                            <li>
                                                                At least 1 lowercase character
                                                            </li>
                                                            <li>
                                                                At least 1 number
                                                            </li>
                                                        </ul>
                                                    </div>
                                                )}
                                                disableProfanityCheck={true}
                                            />
                                            <PasswordValidation
                                                formApi={formApi}
                                                themeV3
                                            />
                                            <Input
                                                label="Confirm New Password"
                                                id="confirmPassword"
                                                field="confirmPassword"
                                                type="password"
                                                placeholder="Confirm New Password"
                                                validate={(value) => checkReEnteredPassword(value, values.password)}
                                                disableProfanityCheck={true}
                                                data-testid="settings.account.confirm-password-input"
                                            />
                                            {hasClubSettings && (
                                                <ToggleSwitch
                                                    id={'playAvailability'}
                                                    field="playAvailability"
                                                    formGroupCssClass={`mt16`}
                                                    label="Match Play Availability"
                                                    onChange={(val) => {
                                                        return updateMatchPlayAvailability(val);
                                                    }}
                                                    reverse={false}
                                                    data-testid="settings.account.play-availability-toggle"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="row bt pb40 pt40">
                                        <div className="col-md-4">
                                            <h5 className="text-uppercase">Sign-in Accounts</h5>
                                        </div>
                                        <div className="col-md-6">
                                            {isTennisAustraliaLoginActive && (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                                    <span className="text-black">Link my Tennis Australia Account</span>
                                                    <ToggleSwitch
                                                        id="linkAustraliaAccount"
                                                        field="linkAustraliaAccount"
                                                        checked={values.linkAustraliaAccount}
                                                        onChange={(e) => handleLinkAustraliaAccount(e, setValue)}
                                                        data-testid="settings.sign-in.link-australia-toggle"
                                                    />
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                                <span className="text-black">Link my Apple Account</span>
                                                <ToggleSwitch
                                                    id="linkAppleAccount"
                                                    field="linkAppleAccount"
                                                    checked={values.linkAppleAccount}
                                                    onChange={(e) => handleLinkAppleAccount(e, setValue)}
                                                    data-testid="settings.sign-in.link-apple-toggle"
                                                />
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span className="text-black">Link my Facebook Account</span>
                                                <ToggleSwitch
                                                    id="linkFacebookAccount"
                                                    field="linkFacebookAccount"
                                                    checked={values.linkFacebookAccount}
                                                    onChange={(e) => handleLinkFacebookAccount(e, setValue)}
                                                    data-testid="settings.sign-in.link-facebook-toggle"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {(isAustralian || auth?.australiaSettings?.tennisConnectId) && (
                                        <AustraliaForm
                                            memberId={auth?.user?.id}
                                            australiaTennisConnectId={auth?.australiaSettings?.tennisConnectId}
                                            formApi={formApi}
                                        />
                                    )}
                                </div>
                                <div className="container buttons">
                                    <div className="row">
                                        <div className="col-md-6 offset-md-3">
                                            <div className="row">
                                                <div className="col-md-6 mb16">
                                                    <button
                                                        // eslint-disable-next-line no-unused-vars
                                                        onClick={(ev) => {
                                                            if (this.props.history.length > 2) {
                                                                this.props.history.goBack();
                                                            } else {
                                                                window.location.href = '/home';
                                                            }
                                                        }}
                                                        type="button"
                                                        className="btn btn-default btn-xl text-uppercase btn-block"
                                                        data-testid="settings.cancel-btn"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                                <div className="col-md-6 mb16">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary btn-xl text-uppercase btn-block"
                                                        disabled={submitting}
                                                        data-testid="settings.update-btn"
                                                    >
                                                        {submitting ? 'Updating...' : 'Update'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        );
                    }
                }
            </Form>
        );
    }
}

export default requireAuth(SettingsForm);
